import { useEffect, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ChevronRight, ArrowLeft, ArrowUpRight, Phone } from "lucide-react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { Reveal } from "./anim";
import { RevealText, CustomCursor, Magnetic } from "./scroll";
import { SmoothScroll } from "./experience";
import { POSTS, postBySlug } from "./blogData";
import { CONTACT, breadcrumb } from "./marketingData";

export default function BlogPost() {
  const { slug } = useParams();
  const post = postBySlug(slug);
  const heroRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const { scrollYProgress } = useScroll();
  const progressX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  const { scrollYProgress: hp } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(hp, [0, 1], ["0%", "22%"]);
  const heroScale = useTransform(hp, [0, 1], [1, 1.12]);

  if (!post) return <Navigate to="/insights" replace />;

  const more = POSTS.filter((p) => p.slug !== slug).slice(0, 3);
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    image: post.image,
    datePublished: post.date,
    author: { "@type": "Organization", name: "Indian Transport Organisation" },
    publisher: { "@type": "Organization", name: "Indian Transport Organisation", logo: { "@type": "ImageObject", url: "https://itoworldwide.com/ito-logo.png" } },
    mainEntityOfPage: `https://itoworldwide.com/insights/${slug}`,
  };

  return (
    <div className="bg-[#F9F8F6] text-[#1A1A18] font-body antialiased overflow-x-hidden md:[cursor:none]">
      <CustomCursor />
      <SmoothScroll />
      <Seo title={`${post.title} | ITO Insights`} description={post.description} path={`/insights/${slug}`} image={post.image} imageAlt={post.title} type="article" keywords={post.keywords} jsonLd={[
        articleJsonLd,
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Insights", path: "/insights" },
          { name: post.title, path: `/insights/${slug}` },
        ]),
      ]} />
      <motion.div style={{ scaleX: progressX }} className="fixed top-0 left-0 right-0 h-1 bg-[#C84B31] origin-left z-[60]" />
      <MarketingNav heroDark />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[62vh] flex items-end pb-14 pt-32 overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0 will-change-transform">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/90 via-[#1A1A18]/55 to-[#1A1A18]/25" />
        </motion.div>
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 w-full">
          <div className="flex items-center gap-2 text-xs text-white/80 mb-5">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/insights" className="hover:text-white">Insights</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#D4A373]">{post.tag}</span>
          </div>
          <span className="inline-block rounded-full bg-[#C84B31] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white mb-5">{post.tag}</span>
          <RevealText as="h1" className="font-heading font-black tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.06]" text={post.title} />
          <div className="mt-5 text-sm text-white/85">
            {new Date(post.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} · {post.readTime}
          </div>
        </div>
      </section>

      {/* BODY */}
      <article className="py-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <p className="text-xl font-heading font-medium leading-snug text-[#1A1A18] mb-10">{post.excerpt}</p>
          {post.body.map((block, i) => (
            <Reveal key={i} className="mb-9">
              <h2 className="font-heading font-bold text-2xl mb-4">{block.h}</h2>
              {block.p.map((para, j) => (
                <p key={j} className="text-[#3a3934] leading-relaxed mb-4 text-[17px]">{para}</p>
              ))}
            </Reveal>
          ))}

          {/* CTA */}
          <div className="mt-12 rounded-2xl bg-[#1A1A18] text-white p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <div className="font-heading font-bold text-xl">Have a load like this to move?</div>
              <p className="text-white/85 text-sm mt-1">Talk to our team for a quote, anywhere in India.</p>
            </div>
            <Magnetic>
              <motion.a whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} href={CONTACT.phoneHref} data-testid="blog-cta" data-cursor
                className="inline-flex items-center gap-2 rounded-full bg-[#C84B31] px-6 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors whitespace-nowrap">
                <Phone className="w-4 h-4" /> Call {CONTACT.phone}
              </motion.a>
            </Magnetic>
          </div>

          <Link to="/insights" data-cursor className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[#C84B31]">
            <ArrowLeft className="w-4 h-4" /> All insights
          </Link>
        </div>
      </article>

      {/* MORE */}
      <section className="py-16 bg-white border-t border-[#1A1A18]/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading font-bold tracking-tight text-2xl md:text-3xl mb-10">More insights</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {more.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link to={`/insights/${p.slug}`} data-testid={`more-post-${p.slug}`} data-cursor
                  className="group block rounded-2xl border border-[#1A1A18]/10 overflow-hidden hover:border-[#C84B31]/50 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <div className="relative h-40 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <div className="p-6">
                    <div className="text-xs text-[#4F4C44] mb-2">{p.readTime}</div>
                    <h3 className="font-heading font-semibold text-lg leading-snug">{p.title}</h3>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C84B31]">
                      Read guide <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
